const AUDIO_MIME_TYPES = {
  aac: 'audio/aac',
  flac: 'audio/flac',
  mp4: 'audio/mp4',
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  mpeg: 'audio/mpeg',
  oga: 'audio/ogg',
  ogg: 'audio/ogg',
  opus: 'audio/ogg',
  wav: 'audio/wav',
};
const AUDIO_EXTENSION_PATTERN = new RegExp(
  `\\.(${Object.keys(AUDIO_MIME_TYPES).join('|')})(?:$|[?#])`,
  'i'
);

const attachmentValue = (attachment, ...keys) =>
  keys
    .map(key => attachment?.[key])
    .find(value => value !== null && value !== undefined && value !== '');

const attachmentFileReference = attachment =>
  String(
    attachmentValue(
      attachment,
      'fileName',
      'file_name',
      'name',
      'dataUrl',
      'data_url',
      'fileUrl',
      'file_url'
    ) || ''
  );

const attachmentExtension = attachment => {
  const declaredExtension = String(
    attachmentValue(attachment, 'extension') || ''
  )
    .replace(/^\./, '')
    .toLowerCase();
  if (declaredExtension) return declaredExtension;

  return attachmentFileReference(attachment)
    .match(/\.([a-z0-9]+)(?:$|[?#])/i)?.[1]
    ?.toLowerCase();
};

export const isAudioAttachment = attachment => {
  const fileType = String(
    attachmentValue(attachment, 'fileType', 'file_type') || ''
  ).toLowerCase();
  const contentType = String(
    attachmentValue(
      attachment,
      'contentType',
      'content_type',
      'mimeType',
      'mime_type'
    ) || ''
  ).toLowerCase();
  const fileReference = attachmentFileReference(attachment);
  const extension = attachmentExtension(attachment);

  // `.mp4` e o conteiner do audio gravado no Safari, mas tambem de todo video.
  // Quem declara video e video: sem isto o compositor rotulava o video como
  // `audio/mp4` e ele saia no WhatsApp como nota de voz (14/09/2026).
  if (fileType === 'video' || contentType.startsWith('video/')) return false;

  return (
    fileType === 'audio' ||
    contentType.startsWith('audio/') ||
    Boolean(AUDIO_MIME_TYPES[extension]) ||
    AUDIO_EXTENSION_PATTERN.test(fileReference)
  );
};

// Aceleres 27/08/2026: o que se REPRODUZ pode nao ser o que se reenvia. O
// WebKit do iPhone nao decodifica Ogg/Opus -- o formato de praticamente todo
// audio da WAME -- e o Chromium so tocava porque fareja o conteiner. O backend
// guarda uma derivada AAC/MP4 e a publica em `playback_url`
// (Aceleres::AudioCompatService); `data_url` segue sendo o original, porque e
// ele que o Laravel reenvia ao WhatsApp e nota de voz exige Ogg/Opus.
//
// A queda para `data_url` cobre dois casos legitimos: a mensagem cujo payload
// antigo ainda esta em memoria, e o audio que ja tocava em todo lugar (MP3,
// M4A) -- para esse o backend devolve a propria `data_url` aqui.
export const audioPlaybackSource = attachment =>
  attachmentValue(
    attachment,
    'playbackUrl',
    'playback_url',
    'dataUrl',
    'data_url'
  ) || '';

export const audioPlaybackContentType = attachment =>
  attachmentValue(
    attachment,
    'playbackContentType',
    'playback_content_type',
    'contentType',
    'content_type'
  ) || '';

export const isUploadedAudioAttachment = ({
  attachment,
  contentAttributes = {},
}) => {
  if (
    contentAttributes.aceleresUploadedAudio ??
    contentAttributes.aceleres_uploaded_audio
  ) {
    return true;
  }

  const wameMediaType = String(
    contentAttributes.wameMediaType ?? contentAttributes.wame_media_type ?? ''
  ).toLowerCase();

  return (
    isAudioAttachment(attachment) &&
    (['document', 'file'].includes(wameMediaType) ||
      ['mp3', 'mpeg'].includes(attachmentExtension(attachment)))
  );
};

export const normalizeUploadedAudioFile = upload => {
  const sourceFile = upload?.file;
  const name = sourceFile?.name || upload?.name || '';

  if (!sourceFile || !AUDIO_EXTENSION_PATTERN.test(name)) return upload;
  if (String(sourceFile.type || upload?.type || '').startsWith('video/')) {
    return upload;
  }

  const extension = name.split('.').pop()?.toLowerCase();
  const normalizedType = AUDIO_MIME_TYPES[extension] || sourceFile.type;
  if (
    sourceFile.type === normalizedType &&
    String(upload.type || normalizedType).startsWith('audio/')
  ) {
    return upload;
  }

  return {
    ...upload,
    type: normalizedType,
    file: new File([sourceFile], name, {
      type: normalizedType,
      lastModified: sourceFile.lastModified,
    }),
  };
};
